import { useEffect, useRef, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { reportClientError } from "../errors/clientErrors";
import { TeamSwitch } from "../team/TeamUi";
import { formatWait, signIn } from "./authApi";
import {
  AuthActions,
  AuthField,
  AuthNotice,
  AuthPage,
  PasswordInput,
  SubmitButton,
  authKitTextKeys,
  describedBy,
  showResult,
} from "./authKit";

/** Claves de texto de la pantalla (las que lee Login.razor, más las del kit: el ojo, el interruptor y «Crear cuenta»). */
export const loginTextKeys = [
  ...authKitTextKeys,
  "LOGIN_TITLE",
  "LOGIN_EMAIL",
  "LOGIN_PASSWORD",
  "LOGIN_SUBMIT",
  "REG_SUBMIT",
  "LOGIN_KEEP_SIGNED_IN",
  "LOGIN_KEEP_SIGNED_IN_OFF",
  "LOGIN_KEEP_SIGNED_IN_ON",
  "CP_AUTH_RECOVERY_GUIDANCE",
  "LOGIN_EMAIL_REQUIRED",
  "LOGIN_EMAIL_FORMAT",
  "LOGIN_PASSWORD_REQUIRED",
  "LOGIN_INVALID",
  "LOGIN_LOCKED_RETRY",
  "LOGIN_LOCKED",
  "LOGIN_RATE_RETRY",
  "LOGIN_RATE_LIMITED",
  "LOGIN_ACCESS_DENIED",
  "LOGIN_SERVICE_UNAVAILABLE",
  "LOGIN_MINUTE",
  "LOGIN_MINUTES",
  "LOGIN_SECOND",
  "LOGIN_SECONDS",
  "ACC_PWD_DONE_LINE",
  "ACC_SESSION_DONE_LINE",
];

/** Por qué se llega al login desde Seguridad (lo deja Seguridad en sessionStorage): se dice una sola vez. */
export const loginExitKey = "bheli-login-exit";
const exitNotice: Record<string, string> = { password: "ACC_PWD_DONE_LINE", session: "ACC_SESSION_DONE_LINE" };

function takeExitReason(): string | null {
  try {
    const reason = sessionStorage.getItem(loginExitKey);
    sessionStorage.removeItem(loginExitKey);
    return reason && exitNotice[reason] ? exitNotice[reason] : null;
  } catch {
    return null;
  }
}

/**
 * Ingreso del Company Portal con las piezas del kit (C03, C04, C05, C09, C14, C21). El servidor valida y decide el paso
 * siguiente (portal, selector de empresa, segundo factor o cambio de clave). Tras un error la contraseña queda vacía y
 * oculta, el correo y el interruptor se conservan; cada error va bajo su campo (C05, sin resumen arriba) y el foco va al
 * primer campo con error. Un rechazo general (credenciales, bloqueo, servicio) es un aviso arriba del formulario.
 */
export function LoginPage({ L }: { L: (key: string) => string }) {
  const [params] = useSearchParams();
  const [exit] = useState(takeExitReason);
  const [email, setEmail] = useState("");
  const [keep, setKeep] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  // Cada respuesta con error vuelve a montar el aviso: un rechazo repetido con el mismo texto se anuncia otra vez.
  const [attempt, setAttempt] = useState(0);
  const password = useRef<HTMLInputElement>(null);
  const busy = useRef(false);
  const title = L("LOGIN_TITLE");
  useEffect(() => {
    document.title = title;
  }, [title]);

  const showFailure = (fieldErrors: Record<string, string>, message: string | null, hasFieldErrors: boolean) =>
    showResult(() => {
      setFields(fieldErrors);
      setError(message);
      setAttempt((value) => value + 1);
    }, hasFieldErrors);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy.current) return;
    busy.current = true;
    setSending(true);
    let leaving = false;
    try {
      const returnOrg = Number(params.get("returnOrg"));
      let result: Awaited<ReturnType<typeof signIn>>;
      try {
        result = await signIn({
          email,
          password: password.current?.value ?? "",
          keepSignedIn: keep,
          returnUrl: params.get("ReturnUrl"),
          returnOrg: Number.isInteger(returnOrg) && returnOrg > 0 ? returnOrg : null,
        });
      } catch (failure) {
        // Sin red o sin respuesta legible: el aviso de servicio no disponible y el error a Error Hub (sin datos del form).
        reportClientError("LOGIN_REQUEST_FAILED", failure instanceof Error ? failure.message : failure);
        showFailure({}, L("LOGIN_SERVICE_UNAVAILABLE"), false);
        return;
      }
      if (result.next && result.status !== "invalid" && result.status !== "error") {
        // Hasta que cargue la página siguiente el botón sigue cargando y no acepta otro envío.
        leaving = true;
        window.location.assign(result.next);
        return;
      }
      const issues = result.fields ?? [];
      if (password.current) password.current.value = "";
      showFailure(
        Object.fromEntries(issues.map((issue) => [issue.field, L(issue.messageKey)])),
        result.status === "error"
          ? formatWait(L(result.messageKey ?? "LOGIN_SERVICE_UNAVAILABLE"), result.retryAfterSeconds, L)
          : null,
        issues.length > 0,
      );
    } finally {
      if (!leaving) {
        busy.current = false;
        setSending(false);
      }
    }
  };

  return (
    <AuthPage title={title}>
      {error ? (
        <AuthNotice key={attempt} tone="error">
          {error}
        </AuthNotice>
      ) : exit ? (
        <AuthNotice tone="done">{L(exit)}</AuthNotice>
      ) : null}
      <form method="post" noValidate onSubmit={(event) => void submit(event)}>
        <AuthField id="email" label={L("LOGIN_EMAIL")} error={fields.email}>
          <input
            id="email"
            name="form.Email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            type="email"
            autoComplete="username"
            aria-invalid={fields.email ? "true" : undefined}
            aria-describedby={describedBy("email", fields.email)}
          />
        </AuthField>
        <AuthField
          id="password"
          label={L("LOGIN_PASSWORD")}
          error={fields.password}
          help={L("CP_AUTH_RECOVERY_GUIDANCE")}
        >
          <PasswordInput
            id="password"
            name="form.Password"
            autoComplete="current-password"
            inputRef={password}
            error={fields.password}
            help={L("CP_AUTH_RECOVERY_GUIDANCE")}
            L={L}
          />
        </AuthField>
        {/* C09: se aplica al ingresar; una línea dice qué pasa en el estado actual. */}
        <TeamSwitch
          title={L("LOGIN_KEEP_SIGNED_IN")}
          line={L(keep ? "LOGIN_KEEP_SIGNED_IN_ON" : "LOGIN_KEEP_SIGNED_IN_OFF")}
          checked={keep}
          onChange={setKeep}
        />
        <AuthActions
          secondary={
            <a className="tm-btn secondary" href="/register">
              {L("REG_SUBMIT")}
            </a>
          }
        >
          <SubmitButton sending={sending}>{L("LOGIN_SUBMIT")}</SubmitButton>
        </AuthActions>
      </form>
    </AuthPage>
  );
}
